import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle, XCircle, Timer, Flask, ClockCounterClockwise, ArrowLeft } from '@phosphor-icons/react';

const RESULT_PILL = {
    matched: { label: 'Matched', cls: 'bg-emerald-50 text-emerald-800 border-emerald-200', Icon: CheckCircle },
    did_not_match: { label: 'Did not match', cls: 'bg-slate-100 text-slate-700 border-slate-200', Icon: XCircle },
    skipped: { label: 'Skipped', cls: 'bg-amber-50 text-amber-900 border-amber-200', Icon: Timer },
};

function ResultPill({ entry }) {
    if (entry.is_test) {
        return <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded border bg-sky-50 text-sky-800 border-sky-200 flex items-center gap-1"><Flask size={11} weight="duotone" /> Test</span>;
    }
    const r = RESULT_PILL[entry.result] || RESULT_PILL.did_not_match;
    const Icon = r.Icon;
    return <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded border flex items-center gap-1 ${r.cls}`}><Icon size={11} weight="duotone" /> {r.label}</span>;
}

export function RuleActivityEntry({ entry, showRule = false }) {
    return (
        <div className="rounded-lg border border-slate-200 bg-card px-4 py-3">
            <div className="flex items-center justify-between gap-2 mb-1">
                <div className="flex items-center gap-2 min-w-0">
                    <ResultPill entry={entry} />
                    {showRule && <div className="text-xs text-slate-500 truncate">{entry.rule_name}</div>}
                </div>
                <div className="text-[11px] text-slate-500 shrink-0">{new Date(entry.fired_at).toLocaleString()}</div>
            </div>
            <div className="text-sm text-slate-800">{entry.trigger_summary}</div>
            {(entry.condition_traces || []).length > 0 && (
                <ul className="mt-2 space-y-1">
                    {entry.condition_traces.map((t, i) => (
                        <li key={i} className="text-xs text-slate-600 flex items-start gap-1.5">
                            <span className={`mt-1 h-1.5 w-1.5 rounded-full ${t.matched ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                            <span>{t.reason}</span>
                        </li>
                    ))}
                </ul>
            )}
            {entry.result === 'did_not_match' && (!entry.condition_traces || entry.condition_traces.length === 0) && (
                <div className="text-xs text-slate-500 mt-1">{entry.reason}</div>
            )}
            {entry.result === 'skipped' && (
                <div className="text-xs text-slate-600 mt-1">{entry.reason}</div>
            )}
            {(entry.actions || []).length > 0 && (
                <div className="mt-2 space-y-1.5 pl-2 border-l-2 border-primary/40">
                    {entry.actions.map((a, i) => (
                        <div key={i} className="text-xs text-slate-700">
                            {a.executed ? '→' : '↷ preview:'} {actionSummary(a)}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function actionSummary(a) {
    const p = a.preview || {};
    if (a.kind === 'add_todo') return <>added to-do <span className="font-medium">“{p.title}”</span></>;
    if (a.kind === 'needs_attention') return <>marked Needs attention: <span className="font-medium">“{p.note}”</span></>;
    if (a.kind === 'send_check_in') return <>sent check-in</>;
    if (a.kind === 'post_hero_card') return <>posted hero card ({p.style}): <span className="font-medium">“{p.message}”</span></>;
    return a.kind;
}

export default function RuleActivity() {
    const [ruleFilter, setRuleFilter] = useState('all');
    const [memberFilter, setMemberFilter] = useState('all');
    const [resultFilter, setResultFilter] = useState('all');
    const { data: rules = [] } = useQuery({ queryKey: ['rules'], queryFn: async () => (await api.get('/rules')).data });
    const { data: facilities = [] } = useQuery({ queryKey: ['facilities'], queryFn: async () => (await api.get('/facilities')).data });
    const facIds = facilities.map(f => f.facility_id);
    const { data: allPatients = [] } = useQuery({
        queryKey: ['all-patients', facIds.join(',')],
        queryFn: async () => {
            const lists = await Promise.all(facIds.map(id => api.get('/patients', { params: { facility_id: id } }).then(r => r.data)));
            return lists.flat();
        },
        enabled: facIds.length > 0,
    });
    const params = {};
    if (ruleFilter !== 'all') params.rule_id = ruleFilter;
    if (memberFilter !== 'all') params.patient_id = memberFilter;
    if (resultFilter !== 'all') params.result = resultFilter;
    const { data: entries = [], isLoading } = useQuery({
        queryKey: ['rule-activity', ruleFilter, memberFilter, resultFilter],
        queryFn: async () => (await api.get('/rule-activity', { params })).data,
    });
    return (
        <div className="px-6 md:px-10 py-8 max-w-5xl" data-testid="rule-activity-root">
            <Link to="/reminders" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 mb-4">
                <ArrowLeft size={12} /> Back to Reminders & Rules
            </Link>
            <header className="mb-6">
                <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 flex items-center gap-2">
                    <ClockCounterClockwise size={12} weight="duotone" className="text-primary" /> Reminders & Rules
                </div>
                <h1 className="font-heading text-4xl md:text-5xl text-slate-900 mt-1">Rule activity</h1>
                <p className="text-slate-600 mt-2 max-w-2xl text-sm">Every rule evaluation, newest first. Use this to see exactly why a rule did or didn’t fire.</p>
            </header>

            <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="min-w-[200px]">
                    <Select value={ruleFilter} onValueChange={setRuleFilter}>
                        <SelectTrigger data-testid="ra-rule-filter"><SelectValue placeholder="All rules" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All rules</SelectItem>
                            {rules.map(r => <SelectItem key={r.rule_id} value={r.rule_id}>{r.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div className="min-w-[200px]">
                    <Select value={memberFilter} onValueChange={setMemberFilter}>
                        <SelectTrigger data-testid="ra-member-filter"><SelectValue placeholder="All family members" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All family members</SelectItem>
                            {allPatients.map(p => <SelectItem key={p.patient_id} value={p.patient_id}>{p.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div className="min-w-[180px]">
                    <Select value={resultFilter} onValueChange={setResultFilter}>
                        <SelectTrigger data-testid="ra-result-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All results</SelectItem>
                            <SelectItem value="matched">Matched</SelectItem>
                            <SelectItem value="did_not_match">Did not match</SelectItem>
                            <SelectItem value="skipped">Skipped</SelectItem>
                            <SelectItem value="test">Test</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="text-xs text-slate-500 ml-auto">{entries.length} entr{entries.length === 1 ? 'y' : 'ies'}</div>
            </div>

            {isLoading ? (
                <div className="text-sm text-slate-500 py-10 text-center">Loading…</div>
            ) : entries.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-300 py-16 text-center text-sm text-slate-500">
                    No rule activity yet. When a check-in answer comes in, matching rules will show up here.
                </div>
            ) : (
                <div className="space-y-2" data-testid="rule-activity-list">
                    {entries.map(e => (
                        <RuleActivityEntry key={e.activity_id} entry={e} showRule={ruleFilter === 'all'} />
                    ))}
                </div>
            )}
        </div>
    );
}
